import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./ModuleDashboard.css";

function StatusDonut({ data = [] }) {
  if (!data.length) {
    return <div className="ai-dash__chart-empty">No status data</div>;
  }

  return (
    <div className="ai-dash__donut-wrap">
      <div className="ai-dash__donut-chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="88%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={entry.color || "#3B82F6"} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="ai-dash__donut-legend">
        {data.map((entry) => (
          <div key={entry.name} className="ai-dash__donut-legend-row">
            <span className="ai-dash__donut-dot" style={{ background: entry.color }} />
            <span className="ai-dash__donut-name" title={entry.name}>
              {entry.name.length > 8 ? `${entry.name.slice(0, 7)}…` : entry.name}
            </span>
            <span className="ai-dash__donut-pct">{entry.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryBars({ rows = [] }) {
  if (!rows.length) {
    return <div className="ai-dash__chart-empty">No category data</div>;
  }

  return (
    <div className="ai-dash__bars">
      {rows.map((row) => (
        <div key={row.name} className="ai-dash__bar-row">
          <span className="ai-dash__bar-label">{row.name}</span>
          <div className="ai-dash__bar-track">
            <div className="ai-dash__bar-fill" style={{ width: `${row.pct}%` }} />
          </div>
          <span className="ai-dash__bar-value">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Module dashboard panel matching the AI Assistant PO/ASN/GRN/Sales/Shipment layout.
 * Supports single-order and multi-order aggregated views.
 */
export default function ModuleDashboard({ data }) {
  if (!data) return null;

  const isMulti = Boolean(data.isMulti);
  const showOrderColumn = Boolean(data.showOrderColumn);

  return (
    <div className="ai-dash">
      <div className="ai-dash__title-row">
        <div className="ai-dash__title-left">
          <span className="ai-dash__grid-icon" aria-hidden="true">
            <i className="bi bi-grid-3x3-gap-fill" />
          </span>
          <div>
            <h3 className="ai-dash__title">{data.title}</h3>
            <p className="ai-dash__subtitle">{data.subtitle}</p>
          </div>
        </div>
        {data.live && <span className="ai-dash__live">LIVE</span>}
      </div>

      <div className="ai-dash__summary">
        {(data.summary || []).map((field) => (
          <div key={field.label} className="ai-dash__summary-item">
            <span className="ai-dash__summary-label">{field.label}</span>
            <span
              className={`ai-dash__summary-value${
                field.accent ? " ai-dash__summary-value--accent" : ""
              }${field.status ? " ai-dash__summary-value--status" : ""}`}
            >
              {field.value}
            </span>
          </div>
        ))}
      </div>

      <div className="ai-dash__kpis">
        {(data.kpis || []).map((kpi) => (
          <div key={kpi.label} className={`ai-dash__kpi ai-dash__kpi--${kpi.tone || "blue"}`}>
            <span className="ai-dash__kpi-label">{kpi.label}</span>
            <span className="ai-dash__kpi-value">{kpi.value}</span>
          </div>
        ))}
      </div>

      <div className="ai-dash__charts">
        <div className="ai-dash__chart-card">
          <h4 className="ai-dash__chart-title">Status</h4>
          <StatusDonut data={data.statusPie} />
        </div>
        <div className="ai-dash__chart-card">
          <h4 className="ai-dash__chart-title">{data.categoryTitle || "Spend by Category"}</h4>
          <CategoryBars rows={data.categoryBars} />
        </div>
      </div>

      {isMulti && Array.isArray(data.records) && data.records.length > 0 && (
        <div className="ai-dash__lines">
          <div className="ai-dash__lines-header">
            <div className="ai-dash__lines-title">
              <i className="bi bi-list-ul" aria-hidden="true" />
              <span>Orders</span>
            </div>
            <span className="ai-dash__lines-count">{data.records.length} Order(s)</span>
          </div>
          <div className="ai-dash__table-wrap">
            <table className="ai-dash__table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Party</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((row) => (
                  <tr key={row.id}>
                    <td className="ai-dash__sku">{row.id}</td>
                    <td>{row.party}</td>
                    <td>{row.date}</td>
                    <td>{row.status}</td>
                    <td>{row.amount}</td>
                    <td>{row.items}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ai-dash__lines">
        <div className="ai-dash__lines-header">
          <div className="ai-dash__lines-title">
            <i className="bi bi-box-seam" aria-hidden="true" />
            <span>Line Items (SKU &amp; Qty)</span>
          </div>
          <span className="ai-dash__lines-count">{data.lineItemCount || 0} Item(s)</span>
        </div>

        {(data.lineItems || []).length === 0 ? (
          <div className="ai-dash__lines-empty">No SKU / line items found on this record.</div>
        ) : (
          <div className="ai-dash__table-wrap">
            <table className="ai-dash__table">
              <thead>
                <tr>
                  {showOrderColumn && <th>Order</th>}
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {data.lineItems.map((row, idx) => (
                  <tr key={`${row.orderId || ""}-${row.sku}-${idx}`}>
                    {showOrderColumn && <td className="ai-dash__sku">{row.orderId}</td>}
                    <td className="ai-dash__sku">{row.sku}</td>
                    <td>{row.description}</td>
                    <td>{row.qty}</td>
                    <td>{row.unitPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
